import * as sessionReplay from '@amplitude/session-replay-browser';
/* eslint-disable */
// DO NOT REMOVE - handles Lazy Loading of images the lead to side effects if not imported and configured
import * as IntersectionObserver from 'intersection-observer';
import Cookies from 'js-cookie';
import { get } from 'lodash';
import { CAMPAIGN_NAMES } from 'constants/personalizations';
import { SHOP_IDS } from 'constants/shops';
import ImageUtil from 'utils/Images';
import $ from 'cash-dom';
import { AMPLITUDE } from 'constants/analytics';
/* eslint-enable */

import AppController from 'controllers/AppController';
import BrandValuesModuleController from 'controllers/BrandValuesModuleController';
import CartController from 'controllers/CartController';
import ExperimentsController from 'controllers/ExperimentsController';
import FooterController from 'controllers/FooterController';
import GeofencingController from 'controllers/GeofencingController';
import GladlyController from 'controllers/GladlyController';
import JumpToContentController from 'controllers/JumpToContentController';
import LocaleController from 'controllers/LocaleController';
import OneTrustController from 'controllers/OneTrustController';
import PartnershipPageController from 'controllers/PartnershipPageController';
import SignupModalController from 'controllers/SignupModalController';
import TopNavigationController from 'controllers/TopNavigationController';
import VideoPlayerController from 'controllers/VideoPlayerController';
import Accessibility from 'utils/Accessibility';
import AnalyticsMediatorSingleton from 'utils/AnalyticsMediatorSingleton';
import Customer from 'utils/Customer';
import DataDogLogs from 'utils/DataDogLogs';
import Log from 'utils/Log';
import Rudderstack from 'utils/Rudderstack';
import Settings from 'utils/Settings';
import TelemetryLog from 'utils/TelemetryLog';

class ApplicationEntrypoint {
  static get $scope() {
    return $('.page-wrapper');
  }

  static get $loader() {
    return $('body, #main-content');
  }

  static matches(classname) {
    return ApplicationEntrypoint.$scope.hasClass(classname);
  }

  static jumpToHash() {
    try {
      let { hash } = window.location;
      if (hash) {
        hash = hash.replace('#', '');
        const scrollTarget = document.getElementById(hash);
        if (scrollTarget) {
          window.scrollTo({
            top: scrollTarget.getBoundingClientRect().top + window.scrollY,
          });
        }
      }
    } catch (error) {
      Log.error(error, 'ApplicationEntrypoint.jumpToHash failed!');
    }
  }

  static removeLoader() {
    ApplicationEntrypoint.$loader.addClass('loaded');
    ApplicationEntrypoint.jumpToHash();
  }

  static async _waitRudderstackGetUserTraits() {
    let count = 0;
    const maxRetryCount = 20; // 10 seconds of retry
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (window.rudderanalytics?.getUserTraits) {
          clearInterval(interval);
          resolve(true);
        } else {
          if (count >= maxRetryCount) {
            clearInterval(interval);
            resolve(false);
          }
          count++;
        }
      }, 500);
    });
  }

  static async initSessionReplay() {
    return new Promise(async resolve => {
      const isInitialized = await ApplicationEntrypoint._waitRudderstackGetUserTraits();

      if (Settings.amplitudeSessionReplayEnabled && Settings.amplitudeApiKey && isInitialized) {
        const traits = window.rudderanalytics.getUserTraits();

        OneTrustController.hasPerformanceConsent().then(async () => {
          const response = sessionReplay.init(Settings.amplitudeApiKey, {
            deviceId: traits.anonymousId,
            sessionId: window.rudderanalytics.getSessionId(),
            optOut: false,
            sampleRate: Settings.amplitudeSessionReplaySampleRate,
          });
          await response.promise;

          const sessionReplayProperties = sessionReplay.getSessionReplayProperties();

          // Wait for the session replay to be initialized
          setTimeout(() => {
            const sessionId = get(sessionReplayProperties, [AMPLITUDE.SESSION_REPLAY_PROP]);
            const marketingQueryParams = Rudderstack.getMarketingQueryParams();
            Rudderstack.identifyUser({
              email: null,
              additionalTraits: {
                session_replay_session_id: sessionId,
                gclid: marketingQueryParams.gclid,
                msclkid: marketingQueryParams.msclkid,
                fbclid: marketingQueryParams.fbclid,
              },
            });

            Customer.sessionReplaySessionId = sessionId;

            // Add TrackJS metadata
            if (window.TrackJS && sessionId) {
              window.TrackJS.addMetadata(AMPLITUDE.SESSION_REPLAY_PROP, sessionId);
            }

            resolve();
          }, 500);
        });
      } else {
        resolve();
      }
    });
  }

  static async init(store) {
    TelemetryLog.init();

    OneTrustController.init(store);

    OneTrustController.hasFunctionalConsent()
      .then(() => {
        ExperimentsController.init(store);
      })
      .catch(() => {});
    OneTrustController.hasPerformanceConsent()
      .then(() => {
        DataDogLogs.init(store);
      })
      .catch(() => {});

    Rudderstack.init();

    Accessibility.trackKeyboardFocus();

    if (ApplicationEntrypoint.matches('PartnershipPage')) {
      PartnershipPageController.init(store);
    }

    TopNavigationController.init(store);
    Customer.identifyPageloadEmail();
    FooterController.init(store);

    const marketingQueryParams = Rudderstack.getMarketingQueryParams();
    /**
     *
     * Warning! - Mediator was disabled and replaced with Rudderstack V3
     * TODO: All code under Settings.isNextGenAnalyticsEnabled shuld be removed
     *
     */
    if (Settings.isNextGenAnalyticsEnabled) {
      const analytics = AnalyticsMediatorSingleton.get();
      analytics.page({
        pageData: window.ALLBIRDS?.page?.template || '',
        meta: { client_platform: 'liquid' },
      });

      // This was added to fix an issue on the Mediator, the identifyUser method is also sending
      //  some contextual traits information that is useful for the analytics
      Rudderstack &&
        Rudderstack.identifyUser &&
        Rudderstack.identifyUser({
          email: null,
          additionalTraits: {
            gclid: marketingQueryParams.gclid,
            msclkid: marketingQueryParams.msclkid,
            fbclid: marketingQueryParams.fbclid,
          },
        });
    } else {
      Rudderstack.identifyUser({
        email: null,
        additionalTraits: {
          gclid: marketingQueryParams.gclid,
          msclkid: marketingQueryParams.msclkid,
          fbclid: marketingQueryParams.fbclid,
        },
      });
      Rudderstack.page(window.ALLBIRDS?.page?.template || '', '', { client_platform: 'liquid' });
    }

    AppController.init(store);
    JumpToContentController.init();
    VideoPlayerController.init();
    GeofencingController.init(store);
    LocaleController.init();
    GladlyController.init();
    if (Settings.shopID === SHOP_IDS.US) {
      SignupModalController.init(store);
      ApplicationEntrypoint.initSessionReplay();
    }
    CartController.init(store);
    BrandValuesModuleController.init(store);
  }
}

export default ApplicationEntrypoint;
